// Library Methods
import {
  createContext,
  useState,
  useMemo,
  useContext,
  useCallback,
} from "react";
import { DateTime } from "luxon";

// Constants
import { DefaultExamCardType } from "src/constants/enums";

// Hooks
import { useAddEditExamHelpers } from "src/hooks/exams/useAddEditExamHelpers";
import { useAddExams } from "src/hooks/exams/useAddExams";
import { useEditExams } from "src/hooks/exams/useEditExams";

// Types
import { ExamType, SelectedExamCardType } from "src/services/exams/types";
import { PatientType } from "src/services/patients/types";

// Contexts
import { useClinicContext } from "../clinic/ClinicProvider";
import { useDevicesContext } from "../devices/DevicesContext";

const ExamSliderContext = createContext({
  scheduledDate: DateTime.now(),
  scheduledError: false,
  currentPatient: {} as PatientType | undefined,
  selectedDevice: { displayName: "", value: "" },
  defaultExamOrder: [] as Array<DefaultExamCardType>,
  defaultSimulationOrder: [] as Array<DefaultExamCardType>,
  unlinkedDeviceOptions: [] as Array<{ displayName: string; value: string }>,
  shouldDisableDeviceOptions: true,
  selectedExamCards: [] as Array<SelectedExamCardType>,
  actions: {
    resetValues: () => {},
    setCurrentPatient: (_: PatientType) => {},
    setScheduledDate: (_: DateTime) => {},
    setScheduledError: (_: boolean) => {},
    setSelectedExamId: (_: string) => {},
    setSelectedDevice: (_: { displayName: string; value: string }) => {},
    setSelectedExamCards: (_: any) => {},
    addExamWrapper: async (_: string) => {},
    editExamWrapper: async (_: string) => {},
    handleInitializeEdit: (_: ExamType) => {},
    onRemoveCard: (_: string, __: number) => {},
  },
});

export const useExamSliderContext = () => {
  const context = useContext(ExamSliderContext);
  if (!context) {
    throw new Error(
      "useExamSliderContext must be used within ExamSliderContextProvider"
    );
  }

  return context;
};

type ExamSliderProviderType = {
  defaultProps?: {
    selectedExamCards?: Array<SelectedExamCardType>;
  };
  children: React.ReactNode;
};

export const ExamSliderProvider = ({
  defaultProps, // used mainly for testing
  children,
}: ExamSliderProviderType) => {
  const { clinicInfo } = useClinicContext();
  const { devices, isLoadingDevices, isFetchingDevices, isRefetchingDevices } =
    useDevicesContext();
  const { defaultExamOrder, defaultSimulationOrder, generateExamsToEdit } =
    useAddEditExamHelpers();
  const { addNewExam } = useAddExams();
  const { editExam } = useEditExams();

  const today = DateTime.now()
    .setZone(clinicInfo?.timezone ?? DateTime.local().zoneName)
    .startOf("day");

  const [currentPatient, setCurrentPatient] = useState<PatientType>();
  const [selectedExamId, setSelectedExamId] = useState("");
  const [scheduledDate, setScheduledDate] = useState<DateTime>(today);
  const [scheduledError, setScheduledError] = useState(false);
  const [selectedDevice, setSelectedDevice] = useState({
    displayName: "",
    value: "",
  });

  const [selectedExamCards, setSelectedExamCards] = useState<
    Array<SelectedExamCardType>
  >(() => {
    const selected = defaultProps?.selectedExamCards ?? [];
    return [...selected]; // in order of selection
  });

  const unlinkedDeviceOptions: Array<{ displayName: string; value: string }> =
    useMemo(
      () =>
        devices
          ?.filter(
            (device) => !device.associatedExam && device.webserviceCompatible
          )
          ?.sort((a, b) => a?.alias?.localeCompare(b?.alias))
          ?.map((device) => ({
            displayName: device.alias,
            value: device.id,
          })) ?? [],
      [devices]
    );

  const shouldDisableDeviceOptions = useMemo(
    () => isLoadingDevices || isFetchingDevices || isRefetchingDevices,
    [isLoadingDevices, isFetchingDevices, isRefetchingDevices]
  );

  const resetValues = useCallback(() => {
    setCurrentPatient(undefined);
    setSelectedExamCards([]);
    setSelectedExamId("");
    setSelectedDevice({ displayName: "", value: "" });
    setScheduledError(false);
    setScheduledDate(today);
  }, [today]);

  // initialize values on the edit exam modal
  const handleInitializeEdit = useCallback(
    (exam: ExamType) => {
      setSelectedExamId(exam.id);
      setCurrentPatient(exam.patient);

      // use the scheduled date at midnight (prevents the date from rolling over)
      if (exam.scheduledDate) {
        const utcString = DateTime.fromISO(exam.scheduledDate, { zone: "utc" });
        const localString = utcString.setZone(
          clinicInfo?.timezone ?? DateTime.local().zoneName
        );
        setScheduledDate(localString);
      }

      const selectedExamCards = generateExamsToEdit(exam);
      setSelectedExamCards([...selectedExamCards]);
    },
    [generateExamsToEdit, clinicInfo?.timezone]
  );

  const addExamWrapper = useCallback(
    async (patientId: string) => {
      return await addNewExam({
        selectedExamCards,
        scheduledDate,
        patientId,
        deviceId: selectedDevice.value,
      });
    },
    [addNewExam, selectedExamCards, scheduledDate, selectedDevice.value]
  );

  const editExamWrapper = useCallback(
    async (patientId: string) => {
      return await editExam({
        selectedExamCards,
        scheduledDate,
        patientId,
        examId: selectedExamId,
        deviceId: selectedDevice.value,
      });
    },
    [
      editExam,
      selectedExamCards,
      selectedExamId,
      scheduledDate,
      selectedDevice.value,
    ]
  );

  // handles clicking the X icon on the exam card
  const onRemoveCard = useCallback(
    (examAlgorithm: string, groupId: number) => {
      setSelectedExamCards((prev: Array<SelectedExamCardType>) => {
        let filteredCards: Array<SelectedExamCardType> = [];
        const hasMultiSameExams =
          prev.filter((exam) => exam.examFieldType === examAlgorithm)?.length >
          1;

        if (hasMultiSameExams) {
          let newIndex = 0;

          // filter out the removed exam and update the groupId values
          filteredCards = prev
            .filter(
              (selectedCard) =>
                selectedCard.examFieldType !== examAlgorithm ||
                (selectedCard.examFieldType === examAlgorithm &&
                  selectedCard.groupId !== groupId)
            )
            .map((selectedCard, index) => {
              if (selectedCard.examFieldType === examAlgorithm) {
                newIndex++;

                return {
                  ...selectedCard,
                  examNumLabel: newIndex,
                  groupId: index,
                };
              }
              return { ...selectedCard, groupId: index };
            });
        } else {
          // update all groupId values when an exam is removed
          filteredCards = prev
            .filter(
              (selectedCard) => selectedCard.examFieldType !== examAlgorithm
            )
            .map((selectedCard, index) => {
              return { ...selectedCard, groupId: index };
            });
        }

        return [...filteredCards];
      });
    },
    [setSelectedExamCards]
  );

  return (
    <ExamSliderContext.Provider
      value={{
        currentPatient,
        selectedDevice,
        scheduledDate,
        scheduledError,
        defaultExamOrder,
        defaultSimulationOrder,
        selectedExamCards,
        unlinkedDeviceOptions,
        shouldDisableDeviceOptions,
        actions: {
          setCurrentPatient,
          setSelectedExamCards,
          setScheduledDate,
          setScheduledError,
          setSelectedDevice,
          setSelectedExamId,
          addExamWrapper,
          handleInitializeEdit,
          editExamWrapper,
          resetValues,
          onRemoveCard,
        },
      }}
    >
      {children}
    </ExamSliderContext.Provider>
  );
};
